import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { motion } from 'framer-motion';

const Footer = () => {
  const { t, language } = useLanguage();
  const { theme } = useTheme();

  const footerLinks = [
    { label: t('terms') || 'Terms of Service', href: '/terms' },
    { label: t('privacy') || 'Privacy Policy', href: '/privacy' },
    { label: t('contact') || 'Contact Us', href: '/contact' },
  ];

  return (
    <footer
      className={`mt-auto bg-gradient-to-r ${
        theme === 'light'
          ? 'from-blue-50 to-gray-100'
          : 'from-slate-800 to-blue-800'
      } border-t border-white/10 backdrop-blur-xl shadow-lg`}
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col md:flex-row justify-between items-center gap-6"
        >
          {/* Copyright and Branding */}
          <div className="text-sm text-gray-500 dark:text-blue-200">
            © 2025 EduTransform Medical Library. {t('allRightsReserved')}
          </div>

          {/* Navigation Links */}
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-sm">
            {footerLinks.map((link) => (
              <motion.a
                key={link.href}
                href={link.href}
                whileHover={{ scale: 1.05, color: '#38bdf8' }}
                transition={{ type: 'spring', stiffness: 300 }}
                className={`$
                  theme === 'light' ? 'text-gray-600' : 'text-blue-200'
                } hover:text-sky-400 transition-colors duration-200 relative group`}
              >
                {link.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-sky-400 to-purple-500 transition-all duration-300 group-hover:w-full" />
              </motion.a>
            ))}
          </div>

          {/* Developed by Ayoub */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ scale: 1.05 }}
            className="relative text-center"
          >
            <span
              className={`text-sm font-medium ${
                theme === 'light'
                  ? 'text-gray-700 bg-gradient-to-r from-blue-100 to-purple-100'
                  : 'text-white bg-gradient-to-r from-blue-600 to-purple-600'
              } px-4 py-2 rounded-full shadow-lg border border-white/20 relative overflow-hidden`}
            >
              {t('developedBy')}
              <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-purple-500">
                {' Ayoub'}
              </span>
              <span
                className={`absolute inset-0 border-2 rounded-full animate-pulse ${
                  theme === 'light' ? 'border-blue-200' : 'border-blue-400'
                } opacity-50`}
              />
            </span>
            <div className="absolute inset-0 -z-10 bg-gradient-to-r from-sky-400/20 to-purple-500/20 blur-xl" />
          </motion.div>
        </motion.div>

        {/* Powered By and Version */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-6 text-center text-xs text-gray-500 dark:text-blue-300"
        >
          {t('poweredBy')} <span className="mx-2">•</span> {t('version')} 1.0.0
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;